import {
  CustomMappingType,
  IFailedCustomMappingRecord,
  type ICustomMapping,
  type ICustomMappingSourceOption,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import { Brand } from '@principle-theorem/principle-core';
import { type ITag } from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  IBlobFilenamePair,
  XSLXImporterExporter,
  asyncForEach,
  multiMap,
  multiSortBy$,
  nameSorter,
  snapshot,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { sortBy } from 'lodash';
import { from } from 'rxjs';
import { reduce } from 'rxjs/operators';
import { BaseCustomMappingHandler } from '../../../base-custom-mapping-handler';
import { CustomMapping } from '../../../custom-mapping';
import { TranslationMapHandler } from '../../../translation-map';
import { AppointmentTagSourceEntity } from '../../source/entities/appointment-tags';
import { AppointmentTagsToXSLX } from './appointment-tags-to-xlsx';
import { XSLXToAppointmentTags } from './xlsx-to-appointment-tags';
import { PracticeMigration } from '../../../practice-migrations';

export const APPOINTMENT_TAG_CUSTOM_MAPPING_TYPE = 'appointmentTagMapping';

export const APPOINTMENT_TAG_MAPPING: ICustomMapping = CustomMapping.init({
  metadata: {
    label: 'Appointment Tags',
    description: `Used for mapping appointment tags to Principle.`,
    type: APPOINTMENT_TAG_CUSTOM_MAPPING_TYPE,
  },
  type: CustomMappingType.DocumentReference,
  labelOverrides: {
    sourceLabel: 'Source Tag',
    destinationIdentifier: 'Principle Appointment Tag',
  },
});

export class CorePracticeAppointmentTagMappingHandler extends BaseCustomMappingHandler<ITag> {
  customMapping = APPOINTMENT_TAG_MAPPING;

  async getSourceOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ICustomMappingSourceOption[]> {
    const tags = await this._getAppointmentTagOptions(migration);
    return sortBy(
      tags.map((tag) => ({
        label: tag,
        value: tag,
      })),
      'value'
    );
  }

  async getDestinationOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<INamedDocument<ITag>[]> {
    return snapshot(
      from(
        Firestore.getDocs(
          Brand.appointmentTagCol(migration.configuration.brand)
        )
      ).pipe(
        multiMap((user) => ({
          name: user.name,
          ref: user.ref,
        })),
        multiSortBy$(nameSorter())
      )
    );
  }

  async getMappingBlob(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBlobFilenamePair> {
    const { fileName, appointmentTags, translator } =
      await this._getExporterData(migration);

    return new XSLXImporterExporter().getBlob(
      fileName,
      appointmentTags,
      translator
    );
  }

  async downloadMapping(migration: WithRef<IPracticeMigration>): Promise<void> {
    const { fileName, appointmentTags, translator } =
      await this._getExporterData(migration);

    await new XSLXImporterExporter().download(
      fileName,
      appointmentTags,
      translator
    );
  }

  async uploadMapping(
    migration: WithRef<IPracticeMigration>,
    file: File
  ): Promise<void> {
    const items = await new XSLXImporterExporter().parse(
      file,
      new XSLXToAppointmentTags()
    );

    const translationMap = new TranslationMapHandler(migration);
    const sourceOptions = await this.getSourceOptions(migration);
    const appointmentTags = await this.getDestinationOptions(migration);

    const failedRecords: IFailedCustomMappingRecord[] = [];

    await asyncForEach(items, async (item) => {
      const mapTo = item.mapTo;
      const matchingOption = sourceOptions.find(
        (sourceOption) => sourceOption.value === item.tag
      );

      if (!matchingOption) {
        return;
      }

      if (!mapTo) {
        return;
      }

      const tag = appointmentTags.find(
        (appointmentTag) => appointmentTag.name === mapTo
      );

      if (!tag) {
        // eslint-disable-next-line no-console
        console.error(
          `Mapping error: ${this.customMapping.metadata.label} - Couldn't find tag for item`,
          item
        );

        failedRecords.push({
          sourceIdentifier: matchingOption.value,
          sourceLabel: matchingOption.label,
          reason: `Destination identifier "${mapTo?.trim()}" not found`,
        });
        return;
      }

      await this.upsertRecord(
        {
          sourceIdentifier: matchingOption.value,
          sourceLabel: matchingOption.label,
          destinationIdentifier: tag.ref,
        },
        translationMap
      );
    });

    const customMapping = await snapshot(
      PracticeMigration.findCustomMapping$(migration, this.customMapping)
    );
    if (!customMapping) {
      throw new Error('Custom mapping not found for updating failed records');
    }
    await Firestore.patchDoc(customMapping.ref, {
      failedRecords,
    });
  }

  private async _getExporterData(
    migration: WithRef<IPracticeMigration>
  ): Promise<{
    fileName: string;
    appointmentTags: string[];
    translator: AppointmentTagsToXSLX;
  }> {
    const fileName = this.getFileName();
    const appointmentTags = await this._getAppointmentTagOptions(migration);
    const translationMap = new TranslationMapHandler(migration);
    const translator = new AppointmentTagsToXSLX(
      await snapshot(Brand.appointmentTags$(migration.configuration.brand)),
      await this.getRecords(translationMap)
    );
    return { fileName, appointmentTags, translator };
  }

  private async _getAppointmentTagOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<string[]> {
    const sourceEntity = new AppointmentTagSourceEntity();
    const uniqueTags = await sourceEntity
      .getRecords$(migration, 1000)
      .pipe(
        reduce((tags, batch) => {
          batch.forEach((record) => tags.add(record.data.tag));
          return tags;
        }, new Set<string>())
      )
      .toPromise();
    return Array.from(uniqueTags ?? []).sort();
  }
}
